import { z } from 'zod';

/**
 * Preference key validation
 * - Must start with a lowercase letter
 * - Can contain lowercase letters and numbers
 * - Separated by dots
 * - Examples: "ui.sidebar.collapsed", "view.models.mode"
 */
export const preferenceKeySchema = z
  .string()
  .regex(
    /^[a-z][a-z0-9]*(\.[a-z][a-z0-9]*)*$/,
    'Key must be lowercase, dot-separated (e.g., "ui.sidebar.collapsed")'
  );

/**
 * Preference value validation
 * - Can be any JSON-serializable value
 */
export const preferenceValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  z.array(z.any()),
  z.record(z.string(), z.any()),
]);

/**
 * Schema for setting a single preference
 */
export const setPreferenceSchema = z.object({
  key: preferenceKeySchema,
  value: preferenceValueSchema,
});

/**
 * Schema for setting multiple preferences at once
 */
export const setBulkPreferencesSchema = z.array(setPreferenceSchema);

/**
 * Schema for querying preferences with optional prefix filter
 */
export const preferenceQuerySchema = z.object({
  prefix: z.string().optional(),
});

/**
 * Type exports
 */
export type PreferenceKeyString = z.infer<typeof preferenceKeySchema>;
export type PreferenceValue = z.infer<typeof preferenceValueSchema>;
export type SetPreferenceInput = z.infer<typeof setPreferenceSchema>;
export type SetBulkPreferencesInput = z.infer<typeof setBulkPreferencesSchema>;
export type PreferenceQuery = z.infer<typeof preferenceQuerySchema>;
